import React, { useEffect, useState } from 'react';
import { Ticket, QrCode, User2, ChevronDown, ChevronUp, MapPin } from 'lucide-react';
import config from '../config';
import { jwtDecode } from 'jwt-decode';

import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

const OffersList = ({
  searchQuery = '',
  setActiveSidebarTab,
  setMapListView,
  setIsSidebarOpen,
  setSelectedPlace,
  map,
  getMapboxCoords
}) => {
  const [offers, setOffers] = useState([]);
  const [expandedOfferId, setExpandedOfferId] = useState(() => {
    const hash = window.location.hash;
    return hash.startsWith('#offer-') ? hash.replace('#offer-', '') : null;
  });

  const [userId, setUserId] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const preferredLanguage = localStorage.getItem('preferredLanguage') || 'en';

  const [user, setUser] = useState(null);
  const token = localStorage.getItem('token');

  // Add useTranslation hook
  const { t } = useTranslation();

  useEffect(() => {
    // Get current user ID from JWT
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserId(decoded.userId);
      } catch (e) {
        console.error('Invalid token');
      }
    }
  }, []);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (token) {
        try {
          const res = await fetch(config.getApiUrl('users/me'), {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          const data = await res.json();

          if (res.ok && data.user) {
            setUser(data.user);
          } else {
            console.error('Error loading user profile');
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
        }
      }
    };

    fetchUserProfile();
  }, [token]);

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        const res = await fetch(config.getApiUrl('offers'));
        const data = await res.json();

        if (data.success) {
          setOffers(data.offers);
        }
      } catch (err) {
        console.error('Error fetching offers:', err);
      }
    };

    const fetchRedemptions = async () => {
      if (!userId) return;
      try {
        const res = await fetch(config.getApiUrl(`redemptions/user/${userId}`));
        const data = await res.json();
        if (data.success) {
          setRedemptions(data.redemptions.map(r => r.offerId));
        }
      } catch (err) {
        console.error('Error fetching redemptions:', err);
      }
    };

    fetchOffers();
    fetchRedemptions();
  }, [userId]);

  const isRedeemed = (offerId) => redemptions.includes(offerId);
  const [imageFailed, setImageFailed] = useState(false);

  const filteredOffers = offers
  .filter((offer) => !isRedeemed(offer._id))
  .filter((offer) => {
    const name = offer.name?.[preferredLanguage] || offer.name?.en || '';
    const description = offer.description?.[preferredLanguage] || offer.description?.en || '';
    return name.toLowerCase().includes(searchQuery.toLowerCase()) ||
           description.toLowerCase().includes(searchQuery.toLowerCase());
  });

  if (!user) {
    return (
      <div className="p-6 text-center text-gray-600">
          {t('offer.loginToView', 'Please log in to view available offers.')}

          <div>
              <button
              onClick={() => {
                  setActiveSidebarTab('profile');
                  setMapListView('list');
                  setIsSidebarOpen(true);
              }}
              className="mt-2 text-blue-600 underline text-sm"
              >
              {t('profile.goToProfile', 'Go to Profile')}
              </button>
          </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {filteredOffers.map((offer) => {
        const name = offer.name?.[preferredLanguage] || offer.name?.en || 'Unnamed';
        const description = offer.description?.[preferredLanguage] || offer.description?.en || '';
        const isExpanded = expandedOfferId === offer._id;
  
        return (
          <div
            key={offer._id}
            className="border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition relative"
          >
            <div
              className="flex items-center justify-between mb-2 cursor-pointer"
              onClick={() => setExpandedOfferId(isExpanded ? null : offer._id)}
            >
              <div className="flex items-center space-x-2">
                {offer.image ? (
                  <img
                    src={offer.image}
                    alt={name}
                    className="h-10 w-10 rounded-full object-cover border border-gray-300"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <Ticket className="h-5 w-5 text-green-500" />
                )}
  
                <h3 className="text-lg font-semibold text-gray-800">{name}</h3>
              </div>
              {isExpanded ? (
                <ChevronUp className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-500" />
              )}
            </div>
  
            <p className="text-sm text-gray-600 mb-2">{description}</p>
  
            {offer.partnerId && (
              <div className="flex items-center text-xs text-gray-500 mb-2">
                <User2 className="w-4 h-4 mr-1" />
                <span>
                  {offer.partnerId.businessName || offer.partnerId.firstName || 'Business'}
                </span>
              </div>
            )}
  
            <div className="inline-block bg-yellow-100 text-yellow-800 text-sm font-semibold px-3 py-1 rounded mt-2">
              {offer.price && offer.price > 0
                ? `💶 ${offer.price.toFixed(2)} €`
                : t('offer.free', 'Free')}
            </div>
  
            {isExpanded && (
              <div className="mt-4 space-y-3">
                {offer.image && (
                  <img
                    src={offer.image}
                    alt={name}
                    className="w-full h-40 object-cover rounded-lg shadow-sm"
                    onError={(e) => (e.target.style.display = 'none')}
                  />
                )}

                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-1">
                    {t('map.showThisCode', 'Show this QR code to redeem the offer')}
                  </p>
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${window.location.origin}/offers/redeem/${offer.qrCode}/${userId}`}
                    alt="QR Code"
                    className="mx-auto rounded shadow"
                  />
                </div>

                {offer.poiId && (
                  <div className="mt-4 space-y-2">
                    {/* POI Photo */}
                    {offer.poiId.photo && (
                      <div className="p-2">
                        <img
                          src={offer.poiId.photo}
                          alt={offer.poiId[`name_${preferredLanguage}`] || offer.poiId.name_en || 'Location'}
                          className="w-full h-32 object-cover rounded-md shadow"
                          onError={(e) => (e.target.style.display = 'none')}
                        />
                      </div>
                    )}

                    {/* POI Name + Map Link */}
                    <div className="flex items-center text-sm text-gray-600">
                      <MapPin className="h-4 w-4 mr-1 text-gray-500" />
                      <span className="mr-2">
                        {offer.poiId[`name_${preferredLanguage}`] || offer.poiId.name_en || 'Location'}
                      </span>
                      <button
                        onClick={() => {
                          if (offer.poiId) {
                            setSelectedPlace(offer.poiId); // highlight this POI
                            const coords = getMapboxCoords(offer.poiId);
                            if (coords && map) {
                              map.flyTo({
                                center: coords,
                                zoom: 25,
                                essential: true
                              });
                            }
                            setActiveSidebarTab('pois'); // switch to POIs tab
                            setMapListView('map');       // show map
                            setIsSidebarOpen(false);     // close sidebar if needed
                          }
                        }}
                        className="text-blue-600 underline text-sm"
                      >
                        {t('map.viewOnMap', 'View on Map')}
                      </button>
                    </div>
                  </div>
                )}


              </div>
            )}

          </div>
        );
      })}
    </div>
  );
};

export default OffersList;
